<script setup lang="ts">
import { computed } from 'vue';
import { SuiIcon } from '@developers/scoro-ui';
import AppConfirmationPopover from '@/features/App/AppConfirmationPopover.vue';

export type ActivationStatus = {
	isActive: boolean;
	statusCode: string;
	statusText: string;
	lastChecked?: string;
};

export type MandateItem = {
	id: string;
	name: string;
	description: string;
	bookmarkId?: string | null;
	bookmarkName?: string | null;
	mandateCode: string;
	fieldMappingId?: string | null;
	fieldMappingName?: string | null;
	activationStatus?: ActivationStatus | null;
};

export type AvalaraMappingsListProps = {
	/**
	 * Module where the bookmark should be opened, e.g. "invoices".
	 * Used to build bookmark URL in the same format as the legacy app: /<module>/?bookmark_id=<id>
	 */
	module?: string;
	mandates: MandateItem[];
};

const props = withDefaults(defineProps<AvalaraMappingsListProps>(), {
	module: 'invoices',
});

const isBillsModule = computed(() => props.module === 'bills');

const getBookmarkHref = (bookmarkId: string): string => {
	const module = String(props.module ?? 'invoices')
		.trim()
		.replace(/^\/+/, '')
		.replace(/\/+$/, '');
	return `/${module}/?bookmark_id=${encodeURIComponent(bookmarkId)}`;
};

const emit = defineEmits<{
	(event: 'edit', mandate: MandateItem): void;
	(event: 'add'): void;
	(event: 'copy', mandate: MandateItem): void;
	(event: 'delete', mandate: MandateItem): void;
}>();

const onEditMandate = (mandate: MandateItem) => {
	emit('edit', mandate);
};

const onAddMandate = () => {
	emit('add');
};

const onCopyMandate = (mandate: MandateItem) => {
	emit('copy', mandate);
};

const onConfirmDelete = (mandate: unknown) => {
	emit('delete', mandate as MandateItem);
};
</script>

<template>
	<div class="avalara-mandates-list">
		<!-- Header Row -->
		<div class="avalara-mandates-list__header">
			<div
				class="avalara-mandates-list__col avalara-mandates-list__col--name"
			>
				{{ $t('name') }}
			</div>
			<div
				class="avalara-mandates-list__col avalara-mandates-list__col--status"
			>
				{{ $t('status') }}
			</div>
			<div
				v-if="!isBillsModule"
				class="avalara-mandates-list__col avalara-mandates-list__col--bookmark"
			>
				{{ $t('bookmark') }}
			</div>
			<div
				class="avalara-mandates-list__col avalara-mandates-list__col--mandate"
			>
				{{ $t('avalara_mandate') }}
			</div>
			<div
				class="avalara-mandates-list__col avalara-mandates-list__col--field-mapping"
			>
				{{ $t('avalara_field_mapping') }}
			</div>
			<div
				class="avalara-mandates-list__col avalara-mandates-list__col--actions"
			/>
		</div>

		<!-- Mandates Container -->
		<div class="avalara-mandates-list__container">
			<!-- Mandate Rows -->
			<div
				v-for="mandate in mandates"
				:key="mandate.id"
				class="avalara-mandates-list__item"
			>
				<div class="avalara-mandates-list__row">
					<div
						class="avalara-mandates-list__col avalara-mandates-list__col--name"
					>
						<span class="avalara-mandates-list__mandate-name">
							{{ mandate.name }}
						</span>
						<span
							v-if="mandate.description"
							class="avalara-mandates-list__mandate-description"
						>
							{{ mandate.description }}
						</span>
					</div>
					<div
						class="avalara-mandates-list__col avalara-mandates-list__col--status"
					>
						<div
							v-if="mandate.activationStatus"
							class="avalara-mandates-list__status-badge"
							:class="{
								'avalara-mandates-list__status-badge--active':
									mandate.activationStatus.isActive,
								'avalara-mandates-list__status-badge--inactive':
									!mandate.activationStatus.isActive,
							}"
							:title="mandate.activationStatus.statusText"
						>
							{{ mandate.activationStatus.statusText }}
						</div>
					</div>
					<div
						v-if="!isBillsModule"
						class="avalara-mandates-list__col avalara-mandates-list__col--bookmark"
					>
						<a
							v-if="mandate.bookmarkId && mandate.bookmarkName"
							class="avalara-mandates-list__bookmark-link"
							:href="getBookmarkHref(mandate.bookmarkId)"
							target="_blank"
							rel="noopener noreferrer"
						>
							{{ mandate.bookmarkName }}
						</a>
					</div>
					<div
						class="avalara-mandates-list__col avalara-mandates-list__col--mandate"
					>
						{{ mandate.mandateCode }}
					</div>
					<div
						class="avalara-mandates-list__col avalara-mandates-list__col--field-mapping"
					>
						{{ mandate.fieldMappingName || '' }}
					</div>
					<div
						class="avalara-mandates-list__col avalara-mandates-list__col--actions"
					>
						<button
							type="button"
							class="avalara-mandates-list__action-btn"
							:title="$t('avalara_edit_mandate')"
							:data-testid="`avalara-mandate-edit-${mandate.id}`"
							@click="onEditMandate(mandate)"
						>
							<SuiIcon name="icon-pencil-16" />
						</button>
						<button
							type="button"
							class="avalara-mandates-list__action-btn"
							:title="$t('avalara_copy_mandate')"
							:data-testid="`avalara-mandate-copy-${mandate.id}`"
							@click="onCopyMandate(mandate)"
						>
							<SuiIcon name="icon-copy-16" />
						</button>
						<div class="avalara-mandates-list__delete-wrapper">
							<AppConfirmationPopover
								v-slot="{ openConfirmation }"
								:title="$t('delete_mapping')"
								message="general_delete_confirmation_message"
								@confirm="onConfirmDelete"
							>
								<button
									type="button"
									class="avalara-mandates-list__action-btn avalara-mandates-list__action-btn--delete"
									:title="$t('avalara_delete_mandate')"
									:data-testid="`avalara-mandate-delete-${mandate.id}`"
									@click="openConfirmation(mandate)"
								>
									<SuiIcon name="icon-bin-16" />
								</button>
							</AppConfirmationPopover>
						</div>
					</div>
				</div>
				<!-- Warning Banner for Inactive Status -->
				<div
					v-if="
						mandate.activationStatus &&
						!mandate.activationStatus.isActive
					"
					class="avalara-mandates-list__warning"
				>
					<div class="avalara-mandates-list__warning-header">
						<div class="avalara-mandates-list__warning-icon">
							<SuiIcon name="icon-alert-24" />
						</div>
					</div>
					<div class="avalara-mandates-list__warning-text">
						{{ $t('avalara_mandate_inactive_warn_text') }}
					</div>
				</div>
			</div>

			<!-- Add Mandate Button -->
			<div class="avalara-mandates-list__add-row">
				<button
					type="button"
					class="avalara-mandates-list__add-btn"
					data-testid="avalara-mandate-add"
					@click="onAddMandate"
				>
					<SuiIcon name="icon-add-16" />
					<span>{{ $t('avalara_add_mandate') }}</span>
				</button>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.avalara-mandates-list {
	display: flex;
	flex-direction: column;
	padding: 1rem 0 3rem 5rem;
	width: 100%;
	box-sizing: border-box;
	min-width: 100rem;

	&__header {
		display: flex;

		// Keep header columns aligned with row columns.
		gap: 2rem;
		align-items: center;

		// Add 1px to left/right padding to account for container border
		padding: 1rem calc(1rem + 1px) 1rem calc(2rem + 1px);
	}

	&__container {
		display: flex;
		flex-direction: column;
		border: 1px solid $neutral-30;
		border-radius: 4px;
		overflow: hidden;
		max-width: 100%;
	}

	&__item {
		display: flex;
		flex-direction: column;
		border-bottom: 1px solid $neutral-30;

		&:last-of-type {
			border-bottom: none;
		}
	}

	&__row {
		display: flex;
		gap: 2rem;
		align-items: center;
		padding: 1.5rem 1rem 1.5rem 2rem;
		flex-wrap: nowrap;
	}

	&__add-row {
		display: flex;
		padding: 0.5rem 1rem;
	}

	&__col {
		font-size: 1.5rem;
		line-height: 2rem;
		color: $neutral-90;
		flex-shrink: 0;

		&--name {
			flex: 1 1 0;
			min-width: 0;
			display: flex;
			flex-direction: column;
			gap: 0.5rem;
			overflow: hidden;
		}

		&--bookmark {
			flex: 0 1 140px;
			min-width: 100px;
			display: flex;
			align-items: center;
		}

		&--mandate {
			flex: 0 1 130px;
			min-width: 100px;
		}

		&--field-mapping {
			flex: 0 1 140px;
			min-width: 100px;
		}

		&--status {
			// Fixed width so header and rows align regardless of content.
			flex: 0 0 12rem;
			min-width: 12rem;
		}

		&--actions {
			// Header has no content, but rows do — keep widths consistent.
			flex: 0 0 14rem;
			min-width: 14rem;
			display: flex;
			align-items: center;
			justify-content: flex-start;
			gap: 0.5rem;
		}
	}

	&__header &__col {
		color: $neutral-70;
		font-size: 1.5rem;
		font-weight: 400;
		line-height: 2rem;
	}

	&__info-icon-wrapper {
		display: inline-flex;
		align-items: center;
		justify-content: center;
	}

	&__info-icon {
		color: $neutral-60;
	}

	&__mandate-name {
		display: block;
		max-width: 100%;
		font-size: 1.625rem;
		font-weight: $font-weight-bold;
		color: $neutral-90;
		line-height: 2.5rem;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__mandate-description {
		display: block;
		max-width: 100%;
		font-size: 1.5rem;
		font-weight: 400;
		color: $neutral-80;
		line-height: 2rem;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__bookmark-link {
		color: $neutral-90;
		text-decoration: none;

		&:visited {
			color: $neutral-90;
		}

		&:hover,
		&:focus-visible {
			color: $blue-50;
			text-decoration: underline;
		}
	}

	&__action-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1rem;
		border: none;
		background: transparent;
		cursor: pointer;
		color: $neutral-60;

		&:hover {
			color: $neutral-70;
		}

		&--delete:hover {
			color: $red-60;
		}
	}

	&__delete-wrapper {
		position: relative;
	}

	&__add-btn {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 1rem;
		border: none;
		background: transparent;
		cursor: pointer;
		color: $neutral-70;
		font-size: 1.5rem;
		font-weight: 400;
		line-height: 2rem;

		&:hover {
			color: $neutral-80;
		}

		:deep(.sui-icon) {
			color: inherit;
		}
	}

	&__status-badge {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.125rem 1rem;
		border: 1px solid;
		border-radius: 4px;
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		font-weight: 500;
		line-height: 2rem;
		white-space: nowrap;

		&--active {
			background-color: #e3f1df;
			color: #173630;
			border-color: #50b83c;
		}

		&--inactive {
			background-color: #fcebdb;
			color: #4a1504;
			border-color: #f49342;
		}
	}

	&__warning {
		display: flex;
		margin: 1.5rem 2rem;
		border: 1px solid $orange-20;
		border-radius: 0.5rem;
		background: $neutral-00;
		overflow: hidden;
		max-width: calc(100% - 4rem);
	}

	&__warning-header {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1rem;
		background: $orange-10;
		border-right: 1px solid $orange-20;
	}

	&__warning-icon {
		display: flex;
		align-items: center;
		justify-content: center;

		:deep(.sui-icon) {
			width: 3rem;
			height: 3rem;
			fill: $orange-50;
			color: $orange-50;
		}
	}

	&__warning-text {
		flex: 1;
		min-width: 0;
		padding: 1.5rem 2rem;
		font-size: 1.625rem;
		line-height: 1.4;
		color: $neutral-90;
	}
}
</style>
